export interface RtdPhase {
  id: number;
  name: string;
  subtitle: string;
  taskIds: string[];
  emoji: string;
  hype: string;
}

export const RTD_PHASES: RtdPhase[] = [
  {
    id: 1,
    name: "Foundation",
    subtitle: "Nail Your Core Identity",
    taskIds: ["geogrid", "category", "description", "services", "service_desc"],
    emoji: "\uD83C\uDFAF",
    hype: "Your core identity is dialed in. Google now knows exactly what you do and who you serve — which means more relevant searches start surfacing your profile.",
  },
  {
    id: 2,
    name: "Profile Strength",
    subtitle: "Build Trust & Completeness",
    taskIds: ["essentials", "reviews", "images"],
    emoji: "\uD83D\uDEE1\uFE0F",
    hype: "Your profile is now trusted, complete, and active. Profiles like yours convert significantly more clicks into calls, directions, and revenue.",
  },
  {
    id: 3,
    name: "Content Engine",
    subtitle: "Feed the Machine",
    taskIds: ["posts", "publish_post", "schedule_post", "products", "publish_product"],
    emoji: "\u26A1",
    hype: "You're now feeding Google fresh signals every week. Active profiles get ranked higher and shown more often — your phone is about to ring more.",
  },
  {
    id: 4,
    name: "Expand",
    subtitle: "Strengthen Your Web",
    taskIds: ["social_sync", "missing_profiles"],
    emoji: "\uD83C\uDF10",
    hype: "Your brand's web of signals is locked in across the platforms Google trusts. This is the prominence boost that separates dominators from the pack.",
  },
];

export function getCompletedPhases(completed: Set<string>): RtdPhase[] {
  return RTD_PHASES.filter((p) => p.taskIds.length > 0 && p.taskIds.every((t) => completed.has(t)));
}

const STORAGE_KEY = (locationId: string) => `dd_celebrated_phases_${locationId}`;

export function getCelebratedPhaseIds(locationId: string): number[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY(locationId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((n) => typeof n === "number") : [];
  } catch {
    return [];
  }
}

export function markPhaseCelebrated(locationId: string, phaseId: number): void {
  try {
    const current = new Set(getCelebratedPhaseIds(locationId));
    current.add(phaseId);
    localStorage.setItem(STORAGE_KEY(locationId), JSON.stringify(Array.from(current).sort()));
  } catch {
    /* localStorage unavailable — silently no-op */
  }
}

/**
 * Returns the FIRST newly-completed phase (smallest id) that has not yet been
 * celebrated for this location. Returns null when there's nothing new.
 */
export function getNewlyCompletedPhase(
  locationId: string,
  completed: Set<string>,
): RtdPhase | null {
  if (!locationId) return null;
  const celebrated = new Set(getCelebratedPhaseIds(locationId));
  for (const phase of getCompletedPhases(completed)) {
    if (!celebrated.has(phase.id)) return phase;
  }
  return null;
}

/**
 * Returns the phase that contains `taskId`. Useful for feature pages that want
 * to know which phase they belong to.
 */
export function getPhaseForTask(taskId: string): RtdPhase | undefined {
  return RTD_PHASES.find((p) => p.taskIds.includes(taskId));
}
